import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {InspectionReport, InspectionOrder} from '../../api/models/inspection.api-model';

export interface InspectionDraft {userId: number; orderId: string; report: InspectionReport; files: {key: string; pointId: string; blob: Blob}[]; dirty: boolean; order?: InspectionOrder;}
@Injectable({providedIn: 'root'})
export class InspectionDraftsService {
  private readonly name = 'wtg-inspection-drafts-v1';
  private readonly pendingKey = 'wtg.inspection.pending';
  private generation = 0;
  constructor(@Inject(PLATFORM_ID) private readonly platform: object) {}
  get pending(): boolean {
    if (!isPlatformBrowser(this.platform)) return false;
    try { return localStorage.getItem(this.pendingKey) === '1'; } catch { return false; }
  }
  private open(): Promise<IDBDatabase> {
    if (!isPlatformBrowser(this.platform) || !('indexedDB' in window)) return Promise.reject(new Error('inspections.errors.storage'));
    return new Promise((resolve, reject) => {
      const r = indexedDB.open(this.name, 1);
      r.onupgradeneeded = () => r.result.createObjectStore('drafts');
      r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error);
    });
  }
  async read(userId: number, orderId: string): Promise<InspectionDraft | null> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const t = db.transaction('drafts', 'readonly'); const r = t.objectStore('drafts').get(`${userId}:${orderId}`);
      r.onsuccess = () => resolve(r.result || null); r.onerror = () => reject(r.error); t.oncomplete = () => db.close(); t.onabort = () => { db.close(); reject(t.error); };
    });
  }
  async save(draft: InspectionDraft): Promise<void> {
    const generation = this.generation; const db = await this.open();
    if (generation !== this.generation) { db.close(); return; }
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction('drafts', 'readwrite'); const store = t.objectStore('drafts'); store.put(draft, `${draft.userId}:${draft.orderId}`); const all = store.getAll();
      t.oncomplete = () => { db.close(); try { localStorage.setItem(this.pendingKey, (all.result as InspectionDraft[]).some(d => d.dirty || d.files.length) ? '1' : '0'); } catch { /* IndexedDB remains authoritative. */ } resolve(); }; t.onerror = () => { db.close(); reject(t.error); }; t.onabort = () => { db.close(); reject(t.error); };
    });

  }
  async remove(userId: number, orderId: string): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction('drafts', 'readwrite'); const store = t.objectStore('drafts'); store.delete(`${userId}:${orderId}`);
      const all = store.getAll();
      t.oncomplete = () => { const pending = (all.result as InspectionDraft[]).some(d => d.dirty || d.files.length); localStorage.setItem(this.pendingKey, pending ? '1' : '0'); db.close(); resolve(); };
      t.onabort = () => { db.close(); reject(t.error); };
    });
  }
  async clear(): Promise<void> {
    if (!isPlatformBrowser(this.platform)) return;
    ++this.generation;
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => { const t = db.transaction('drafts', 'readwrite'); t.objectStore('drafts').clear(); t.oncomplete = () => { db.close(); resolve(); }; t.onabort = () => { db.close(); reject(t.error); }; });
      localStorage.removeItem(this.pendingKey);
    } catch { /* Storage denial must not prevent revoking authentication. */ }
  }
}
